import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';

import getYouthListQuery from '../../queries/redux/getYouthList.graphql';

const USER = window.cccisd && window.cccisd.fortress.user.acting;

const initialState = {
    loading: true,
    youthList: [],
};

// query list of all youth belonging to this current Care Coordinator
export const queryCareCoordinatorYouthList = createAsyncThunk('careCoordinator/queryYouthList', async () => {
    const response = await client.query({
        query: getYouthListQuery,
        fetchPolicy: 'network-only',
        variables: {
            careCoordinatorPawnId: USER.id,
        },
    });

    return _get(response, 'data.roles.youthList', []).map(youth => {
        const fields = { ...youth.fields };
        fields.flags = [];

        // deploymentId : sessionIds
        let youthPlanDeploymentIds = {};

        youth.selectedAssignmentPlanList.forEach(plan => {
            youthPlanDeploymentIds[plan.assignmentPlanId] = {
                sessionList: plan.sessionList.map(session => session.sessionId),
                settings: JSON.parse(plan.settings),
            };
        });

        youth.flags.forEach(flag => {
            const flagParentPlan =
                Object.values(youthPlanDeploymentIds).find(plan => plan.sessionList.includes(flag.sessionId)) || {};

            const planName = _get(flagParentPlan, 'settings.planName', '');
            const planStartDate = _get(flagParentPlan, 'settings.startDate', '');

            flag.flagProgressList.forEach(progress => {
                const matchVariableName = progress.flag.equation?.match(/@(\w+)@/);
                const variableName = matchVariableName ? matchVariableName[1] : null;
                const variableValue = (variableName && flag.variables?.[variableName]) ?? progress.flag.equation;

                fields.flags.push({
                    key: `${youth.pawn.pawnId}_${progress.flagId}`,
                    respondent_pawn_id: youth.pawn.pawnId,
                    critical: progress.flag.name.toLowerCase().includes('critical'),
                    surveyItem: progress.flag.description,
                    response: variableValue,
                    date: progress.flaggedDate,
                    respondent: 'youth',
                    actions: ['resolve', 'addNote', 'viewLogs'],
                    log: progress.log,
                    notes: progress.notes,
                    flagId: progress.flagId,
                    status: progress.status,
                    unflaggedReason: progress.unflaggedReason,
                    flagProgressId: progress.flagProgressId,
                    unflaggedAt: progress.unflaggedAt,
                    wrapId: youth.fields.wrapId,
                    sessionId: flag.sessionId,
                    planName,
                    planStartDate,
                });
            });
        });

        // add the caregiver flags to the flags object
        youth.parentRoles.caregiverList.forEach(caregiver => {
            caregiver.flags.forEach(flag => {
                const sessionId = flag.sessionId;
                // only add caregiver flags that come from this youth's deployments
                if (
                    !Object.values(youthPlanDeploymentIds)
                        .map(plan => plan.sessionList)
                        .flat()
                        .includes(sessionId)
                )
                    return;

                const flagParentPlan =
                    Object.values(youthPlanDeploymentIds).find(plan => plan.sessionList.includes(flag.sessionId)) || {};

                const planName = _get(flagParentPlan, 'settings.planName', '');
                const planStartDate = _get(flagParentPlan, 'settings.startDate', '');

                flag.flagProgressList.forEach(progress => {
                    const matchVariableName = progress.flag.equation?.match(/@(\w+)@/);
                    const variableName = matchVariableName ? matchVariableName[1] : null;
                    const variableValue = (variableName && flag.variables?.[variableName]) ?? progress.flag.equation;

                    fields.flags.push({
                        key: `${caregiver.pawn.pawnId}_${progress.flagId}`,
                        respondent_pawn_id: caregiver.pawn.pawnId,
                        critical: progress.flag.name.toLowerCase().includes('critical'),
                        surveyItem: progress.flag.description,
                        response: variableValue,
                        date: progress.flaggedDate,
                        respondent: 'caregiver',
                        caregiverName: `${caregiver.fields.firstName} ${caregiver.fields.lastName}`,
                        actions: ['resolve', 'addNote', 'viewLogs'],
                        log: progress.log,
                        notes: progress.notes,
                        flagId: progress.flagId,
                        status: progress.status,
                        unflaggedReason: progress.unflaggedReason,
                        flagProgressId: progress.flagProgressId,
                        unflaggedAt: progress.unflaggedAt,
                        wrapId: caregiver.fields.wrapId,
                        sessionId,
                        planName,
                        planStartDate,
                    });
                });
            });
        });

        return { ...youth, flags: fields.flags };
    });
});

const careCoordinatorSlice = createSlice({
    name: 'careCoordinator',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(queryCareCoordinatorYouthList.pending, state => {
                state.loading = true;
            })
            .addCase(queryCareCoordinatorYouthList.fulfilled, (state, action) => {
                state.youthList = action.payload;
                state.loading = false;
            });
    },
});

export default careCoordinatorSlice.reducer;
